<template>
    <div class="white-bg">
        <div class="md-layout">
            <div class="search-bar">
                <input type="text" v-focus v-model="searchText" placeholder="Search by name, company, or email" />
            </div>
            <div class="sort-bar">
                <div class="sort-filter-container">
                    <md-field>
                        <vue-select
                            :reduce="(x) => x.value"
                            label="text"
                            :options="sortByOptions"
                            v-model="sortBy"
                            placeholder="Sort by"
                            :clearable="false"
                        ></vue-select>
                    </md-field>
                </div>

                <div class="floating-button" v-if="isSales">
                    <md-button
                        title="Add Customer"
                        class="md-primary md-just-icon md-round"
                        @click.stop="createCustomer"
                    >
                        <md-icon>add</md-icon>
                    </md-button>
                </div>
            </div>
        </div>
        <div class="md-layout">
            <div class="md-layout-item list-container">
                <div v-if="!loading">
                    <div v-if="searchCustomerList.length">
                        <div v-for="customer in searchCustomerList" :key="customer.customerId" class="list-item">
                            <div class="item-details" @click="toggleSidebar(customer)">
                                <span class="heavy-font-600">{{ customer.name }}</span>
                                <div>{{ customer.address }}</div>
                                <div>
                                    <a
                                        v-if="customer.phone"
                                        title="Customer Phone"
                                        class="md-button md-info md-just-icon md-round"
                                        :href="`tel:${customer.phone}`"
                                    >
                                        <div class="md-ripple">
                                            <div class="md-button-content">
                                                <md-icon>call</md-icon>
                                            </div>
                                        </div>
                                    </a>

                                    <a
                                        v-if="customer.email"
                                        title="Customer Email"
                                        class="md-button md-warning md-just-icon md-round"
                                        :href="`mailto:${customer.email}`"
                                    >
                                        <div class="md-ripple">
                                            <div class="md-button-content">
                                                <md-icon>email</md-icon>
                                            </div>
                                        </div>
                                    </a>

                                    <a
                                        title="Schedule Stop"
                                        class="md-button md-primary md-just-icon md-round"
                                        @click.stop="handleCreateStop(customer.customerId)"
                                    >
                                        <div class="md-ripple">
                                            <div class="md-button-content">
                                                <md-icon>event</md-icon>
                                            </div>
                                        </div>
                                    </a>
                                    <a
                                        title="Add Note"
                                        class="md-button md-rose md-just-icon md-round"
                                        @click.stop="handleAddNotes(customer)"
                                    >
                                        <div class="md-ripple">
                                            <div class="md-button-content">
                                                <md-icon>note_add</md-icon>
                                            </div>
                                        </div>
                                    </a>
                                    <router-link
                                        class="md-button md-primary md-just-icon md-round"
                                        :to="{
                                            name: 'Customer Details',
                                            params: {
                                                customerId: customer.customerId
                                            }
                                        }"
                                    >
                                        <div class="md-ripple">
                                            <div class="md-button-content">
                                                <md-icon>visibility</md-icon>
                                            </div>
                                        </div>
                                    </router-link>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="md-layout-item">
                        <span class="text-center no-result-message">No available customers to display.</span>
                    </div>
                </div>
                <div v-else>
                    <div class="list-loader" v-if="!isLoading">
                        <fade-loader :loading="true" color="#333333" />
                        <span>LOADING</span>
                    </div>
                </div>
            </div>

            <div class="drawer-sidebar">
                <md-drawer :md-active.sync="showSidebar" class="md-right">
                    <md-toolbar class="md-primary">
                        <div class="md-toolbar-row">
                            <md-button class="md-icon-button" @click="showSidebar = false">
                                <md-icon>close</md-icon>
                            </md-button>
                            <h2 class="md-title">Customer Details</h2>
                        </div>
                    </md-toolbar>
                    <div class="sidebar-drawer" v-if="sidebarLoader">
                        <div class="sidebar-loader">
                            <fade-loader :loading="true" color="#333333" />
                            <span>LOADING</span>
                        </div>
                    </div>
                    <div class="drawer-container" v-else-if="sidebarLoader === false && selectedCustomer">
                        <div class="drawer-item">
                            <div class="label">Fullname:</div>
                            <div class="value">{{ selectedCustomer.firstName }} {{ selectedCustomer.lastName }}</div>
                        </div>

                        <div class="drawer-item">
                            <div class="label">Company:</div>
                            <div class="value">{{ selectedCustomer.company }}</div>
                        </div>

                        <div class="drawer-item">
                            <div class="label">Address:</div>
                            <div class="value">{{ selectedCustomer.address }}</div>
                        </div>

                        <div class="drawer-item">
                            <div class="label">Phone:</div>
                            <div class="value">
                                <a :href="`tel:${selectedCustomer.phone}`">
                                    {{ selectedCustomer.phone ? selectedCustomer.phone : '-' }}
                                </a>
                            </div>
                        </div>

                        <div class="drawer-item">
                            <div class="label">Email:</div>
                            <div class="value">
                                <a :href="`tel:${selectedCustomer.email}`">
                                    {{ selectedCustomer.email ? selectedCustomer.email : '-' }}
                                </a>
                            </div>
                        </div>

                        <div class="drawer-item">
                            <div class="label">ABN:</div>
                            <div class="value">{{ selectedCustomer.abn ? selectedCustomer.abn : '-' }}</div>
                        </div>

                        <div class="drawer-item">
                            <div class="label">Customer Notes:</div>
                            <div class="value">{{ selectedCustomer.notes ? selectedCustomer.notes : '-' }}</div>
                        </div>

                        <div class="drawer-item">
                            <div class="label">Latest Notes:</div>
                            <div class="value">
                                <div v-for="note in selectedCustomer.latestNotes" :key="note.noteId">
                                    <div class="note-document">
                                        <div v-if="note.documentUrls != null && note.documentUrls.length > 0">
                                            <div
                                                v-for="(documentUrl, documentIndex) in note.documentUrls"
                                                :key="documentIndex"
                                            >
                                                <a :href="documentUrl.photoUrl" download target="_blank">
                                                    <img
                                                        v-if="$_getFileIcon(documentUrl.photoName)"
                                                        class="file-icon"
                                                        :src="`/img/icons/${$_getFileIcon(documentUrl.photoName)}.png`"
                                                        alt=""
                                                    />
                                                    {{ documentUrl.photoName }}
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="note-photos" v-if="note.photoUrls != null && note.photoUrls.length > 0">
                                        <div class="note-image-container">
                                            <div v-for="(photoUrl, photoIndex) in note.photoUrls" :key="photoIndex">
                                                <a @click="viewPhoto(note.photoUrls, photoUrl)">
                                                    <img
                                                        class="note-image"
                                                        :src="photoUrl.thumbnailUrl"
                                                        @error="$_notePhotoLoading($event, photoUrl.thumbnailUrl)"
                                                    />
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                    <span class="note-date-text">
                                        {{ note.createdDate | dateFormat(DATE_TYPES.standardDate) }}
                                    </span>
                                    <div class="note-preview" v-html="$_compiledMarkdown(note.note)"></div>
                                </div>
                            </div>
                        </div>

                        <div class="drawer-item">
                            <div class="label">Upcoming Visit:</div>
                            <div class="value" v-if="selectedCustomer.upcomingStop">
                                {{ selectedCustomer.upcomingStop.tripDate | dateFormat(DATE_TYPES.standardDate) }},
                                <router-link
                                    :to="{
                                        name: 'Stop Details',
                                        params: { stopId: selectedCustomer.upcomingStop.stopId }
                                    }"
                                >
                                    {{ selectedCustomer.upcomingStop.status }}
                                </router-link>
                            </div>
                        </div>

                        <div class="drawer-item">
                            <div class="label">Last Visit:</div>
                            <div class="value">
                                <div
                                    v-for="previousStop in selectedCustomer.lastVisitedStops"
                                    :key="previousStop.stopId"
                                >
                                    {{ previousStop.tripDate | dateFormat(DATE_TYPES.standardDate) }},
                                    <router-link
                                        :to="{
                                            name: 'Stop Details',
                                            params: { stopId: previousStop.stopId }
                                        }"
                                    >
                                        {{ previousStop.status }}
                                    </router-link>
                                </div>
                            </div>
                        </div>
                    </div>
                </md-drawer>
            </div>
        </div>
    </div>
</template>
<script>
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { mapGetters } from 'vuex';
import FadeLoader from 'vue-spinner/src/FadeLoader';
import { handleRequests, showErrorMessage } from '@/helpers';
import CreateStopModal from '@/pages/Stops/components/CreateStopModal';
import { STOP_DETAILS_DEFAULTS, CUSTOMER_DETAILS_DEFAULTS } from '@/utils/defaults';
import NotesModalMobile from '@/pages/NotesModalMobile';
import CreateCustomerModal from '@/pages/Customer/Modals/CreateCustomerModal';

export default {
    name: 'MobileCustomerOverview',
    mixins: [GeneralMixin],
    components: { FadeLoader },
    data() {
        return {
            searchCustomerList: [],
            loading: false,
            list: [],
            showSidebar: false,
            selectedCustomer: null,
            customerSidebarDetails: {},
            sidebarLoader: false,
            searchText: '',
            sortBy: 'name',
            sortByOptions: [
                { text: 'Sort by Company Name', value: 'name' },
                { text: 'Sort by Newly added customers', value: 'createdDate' }
            ],
            stopDetails: Object.assign({}, STOP_DETAILS_DEFAULTS())
        };
    },
    computed: {
        ...mapGetters({
            user: 'user/user',
            isLoading: 'isLoading',
            isSingleUser: 'user/isIndividualUser',
            isSingleTeamMember: 'team/isSingleTeamMember',
            isSales: 'user/isSales',
            isCustomer: 'user/isCustomer'
        })
    },
    watch: {
        // eslint-disable-next-line func-names
        searchText: _.debounce(function() {
            this.getCustomerList();
        }, 500),
        sortBy(newValue) {
            this.getCustomerList();
        }
    },
    async mounted() {
        await this.getTeamMembers();
        await this.getCustomerList();
    },
    methods: {
        async getCustomerList(pageNumber = 1, itemsPerPage = 50) {
            let response = {
                data: {
                    customers: [],
                    totalCustomers: null
                }
            };
            try {
                this.loading = true;
                const endpoint = `/api/customers`;
                response = await handleRequests(endpoint, {
                    method: 'GET',
                    params: {
                        searchText: this.searchText,
                        sortBy: this.sortBy,
                        disablePaging: true
                    }
                });
                this.searchCustomerList = response.data.customers;
            } catch (error) {
                const message = 'Error in getting the customer list';
                showErrorMessage(this, message, error);
            }
            this.loading = false;
        },
        toggleSidebar(customer) {
            this.showSidebar = !this.showSidebar;
            this.$nextTick(() => {
                this.getCustomerDetails(customer.customerId);
            });
        },
        async getCustomerDetails(customerId) {
            try {
                this.sidebarLoader = true;
                const api = `/api/customers/${customerId}?includeNotes=true&includeStopsHistory=true`;
                const { data } = await handleRequests(api);
                this.selectedCustomer = {};
                Object.assign(this.selectedCustomer, data);
            } catch (error) {
                const message = 'Error in getting the customer details';
                showErrorMessage(this, message, error);
            }
            this.sidebarLoader = false;
        },
        handleAddNotes(customer) {
            this.$modal
                .show(NotesModalMobile, {
                    customerId: customer.customerId
                })
                .then((response) => {
                    this.$emit('notesAdded');
                });
        },
        async createCustomer() {
            const customerDetails = { ...CUSTOMER_DETAILS_DEFAULTS(), teamRegionId: this.user.teamRegionId || null };
            this.$modal
                .show(CreateCustomerModal, {
                    customerDetails
                })
                .then(async (response) => {
                    if (response && response.toLowerCase() === 'ok') {
                        this.$modal.hide();
                        await this.getCustomerList();
                    }
                });
        },
        handleCreateStop(selectedCustomerId) {
            this.resetStopState();

            this.stopDetails.assignToPublicUserId = this.user.publicUserId;
            this.stopDetails.customerId = selectedCustomerId;
            this.stopDetails.durationMinutes = this.user.defaultStopDurationMinutes ?? this.stopDetails.durationMinutes;

            this.$modal
                .show(CreateStopModal, {
                    stopDetails: this.stopDetails,
                    disableCustomerField: true
                })
                .then((response) => {
                    if (response && response.toLowerCase() === 'ok') {
                        this.$modal.hide();
                    }
                });
        },
        resetStopState() {
            Object.assign(this.stopDetails, STOP_DETAILS_DEFAULTS());
        },
        async getTeamMembers() {
            this.teamMembers = await this.$store.dispatch('team/FETCH_TEAM_MEMBERS', {});
        }
    }
};
</script>

<style lang="scss" scoped>
.drawer-container {
    padding: 20px;
    font-size: 16px;
    color: #000;
    max-height: 80vh;
    overflow-y: auto;
    .drawer-item {
        margin-bottom: 15px;
        a {
            color: #2b93ff !important;
        }
        .label {
            color: gray;
            font-weight: 500;
        }
        .value {
            font-size: 18px;
        }

        .note-preview {
            ::v-deep p {
                margin-top: 0;
            }
        }
    }
}
.md-toolbar {
    padding: 0;
    background-color: transparent !important;
    color: black !important;
    box-shadow: 0 4px 20px 0px rgba(0, 0, 0, 0.14), 0 7px 12px -5px rgba(153, 153, 153, 0.46);
    .md-toolbar-row {
        padding-left: 0;
        .md-icon-button {
            background-color: transparent !important;
            position: absolute;
            right: 0;
            i {
                color: black !important;
            }
        }
        .md-title {
            color: black !important;
        }
    }
}
.white-bg,
:v-deep .content {
    background-color: #fff;
}
.no-result-message {
    font-size: 1.4rem;
    display: block;
}
.list-loader,
.sidebar-loader {
    position: absolute;
    top: 35%;
    left: 49%;
    span {
        position: absolute;
        margin-top: 50px;
        width: 110px;
        left: calc((100% - 100px) / 2);
        text-align: center;
        font-weight: 600;
    }
}
.list-loader {
    margin-top: 20%;
}

.list-container {
    padding-top: 20px;
    height: 75vh;
    overflow-y: auto;
}
.list-item {
    margin-bottom: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid #ddd;
    > div {
        display: inline-block;
        vertical-align: middle;
        font-weight: 400;
        color: #3c4858;
    }
    .item-details {
        width: 100%;
        > div {
            vertical-align: middle;
        }
        > div:last-child {
            a > div {
                color: #3c4858;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }
    }
}
.sidebar-drawer {
    position: relative;
    min-height: 50vh;
    .sidebar-loader {
        top: 50%;
        left: 49%;
    }
}

.note-date-text {
    font-size: 14px;
    color: #999;
}
.search-bar,
.sort-bar {
    width: 100%;
    margin-top: 10px;
    padding: 0 10px;
    input {
        width: 100%;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 5px;
        font-size: 16px;
    }

    ::v-deep .md-select {
        height: 40px;
        input {
            font-size: 16px;
            padding: 0 10px;
        }
    }
}

.sort-bar {
    margin-top: 0;
    > div {
        display: inline-block;
        vertical-align: middle;
    }

    .sort-filter-container {
        width: calc(100% - 67px);
        margin-right: 10px;
    }
}

.white-bg {
    min-height: 88vh;
}

::v-deep .vs__selected-options {
    font-size: 16px;
}
</style>
